<template>
  <div class="card report-template-form">
    <div class="card-header">
      {{ form.id ? $t("Edit Template") : $t("Create a New Template") }}
    </div>
    <div class="card-body">
      <b-form @submit.prevent="handleSubmit">
        <b-row>
          <!-- Basic Information -->
          <b-col md="4">
            <h5>{{ $t("Basic Information") }}</h5>
            <b-form-group
              label="Name"
              :state="getFieldState('name')"
              invalid-feedback="Name is required"
            >
              <b-form-input
                v-model="form.name"
                :state="getFieldState('name')"
                required
              ></b-form-input>
            </b-form-group>

            <b-form-group
              v-if="mode === 'service provider'"
              label="Client"
              :state="getFieldState('client_id')"
              invalid-feedback="Please select a client"
            >
              <b-form-select
                v-model="form.client_id"
                :options="formattedClients"
                :state="getFieldState('client_id')"
              ></b-form-select>
            </b-form-group>
          </b-col>

          <!-- Columns Section -->
          <b-col md="8">
            <report-templates-create
              v-if="columns.length"
              :initial-required="form.required"
              :initial-selected="form.selectedColumns"
              :initial-available="form.availableColumns"
              @update:selected-columns="updateSelectedColumns"
              @update:required-columns="updateRequiredColumns"
            ></report-templates-create>
            <div v-else class="alert alert-danger">
              {{ $t("There are no report columns created yet. Please create at least one report column before creating a new report.") }}
            </div>
          </b-col>
        </b-row>

        <!-- Submit Button -->
        <b-button :disabled="loading" variant="primary" type="submit">
          <span
            v-if="loading"
            class="spinner-border spinner-border-sm"
            role="status"
          ></span>
          {{ form.id ? $t("Save Template") : $t("Create Template") }}
        </b-button>
      </b-form>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "ReportTemplateForm",
  props: {
    mode: {
      type: String,
      required: true
    },
    template: {
      type: Object,
      required: true
    },
    clients: {
      type: Array,
      required: true
    },
    columns: {
      type: Array,
      required: true
    },
    formAction: {
      type: String,
      required: true
    }
  },
  data() {
    const selectedColumnIds = (this.template.columns || []).map(
      col => col.report_column_id
    );

    return {
      form: {
        id: this.template.id || null,
        name: this.template.name || "",
        client_id: this.template.client_id || "",
        required: (this.template.columns || [])
          .filter(col => col.required)
          .map(col => col.report_column_id),
        selectedColumns: (this.template.columns || []).map(col => {
          const fullColumn = this.columns.find(
            c => c.id === col.report_column_id
          );
          return {
            ...col,
            ...(fullColumn || {})
          };
        }),
        availableColumns: this.columns.filter(
          col => !selectedColumnIds.includes(col.id)
        )
      },
      errors: {},
      loading: false
    };
  },
  computed: {
    formattedClients() {
      return [
        { value: "", text: this.$t("Select Client") },
        ...this.clients.map(client => ({
          value: client.id,
          text: client.name
        }))
      ];
    }
  },
  methods: {
    updateSelectedColumns(columns) {
      this.form.selectedColumns = columns;
    },
    updateRequiredColumns(required) {
      this.form.required = required;
    },
    getFieldState(field) {
      return this.errors[field] === undefined ? null : false;
    },
    async handleSubmit() {
      this.loading = true;
      this.errors = {};

      try {
        const payload = {
          ...this.form,
          columns: this.form.selectedColumns.map(col => col.id),
          required: this.form.required // Inclure `required` dans les données envoyées
        };

        const method = this.form.id ? "put" : "post";

        await axios[method](this.formAction, payload);

        this.$bvToast.toast(
          this.form.id
            ? this.$t("Template updated successfully!")
            : this.$t("Template created successfully!"),
          {
            title: "Success",
            variant: "success",
            solid: true
          }
        );

        setTimeout(() => {
          window.location.href = "/settings/templates";
        }, 1000);
      } catch (error) {
        if (error.response && error.response.data.errors) {
          this.errors = error.response.data.errors;
        } else {
          this.$bvToast.toast(this.$t("An error occurred. Please try again."), {
            title: "Error",
            variant: "danger",
            solid: true
          });
        }
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
